import { useMemo, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { CatalogProduct, ProductCategory } from "@/types";
import { UNSET_DASH } from "@/lib/format";
import { semanticColors, radius, spacing, heights, typography } from "@/styles/tokens";

const ROW_HEIGHT = heights.row;
const HEADER_HEIGHT = heights.control;

// ⚠️ THIS TABLE RENDERS THE REAL PRODUCT MASTER (slice 1c), not
// mocks/products.ts. Three columns went with that cutover and none of them
// should come back on the strength of "the layout looks sparse":
//
//   spec       anixify.product has no such column and never will. A column
//              that is structurally a dash on every row forever is 220px of
//              permanent noise, not a placeholder for future data.
//   listPrice  There is no catalog price ANYWHERE in this system -- every
//              price comes from the engine, computed from the six inputs on
//              a quote line. A second place a number could come from is the
//              bug this whole design exists to avoid. The fact is stated
//              once, in prose, under the count on Catalog.tsx.
//   stock      Not absent -- just not on this screen yet. Inventory is its
//              own slice. A dash here would say "we do not know", which is a
//              different and untrue claim.
//
// ⚠️ EVERY REMAINING NULLABLE COLUMN RENDERS THE DASH, NEVER A ZERO AND NEVER
// AN EMPTY CELL. Absent is not zero, and an empty cell reads as a rendering
// glitch rather than as a fact about the record.
type ColDef = {
  field: keyof CatalogProduct;
  header: string;
  width: number;
  align: "left" | "right";
  mono: boolean;
  frozen: boolean;
};

const COLS: ColDef[] = [
  { field: "partNo",        header: "Part No.",     width: 160, align: "left",  mono: false, frozen: true  },
  { field: "description",   header: "Description",  width: 240, align: "left",  mono: false, frozen: true  },
  { field: "brand",         header: "Brand",        width: 100, align: "left",  mono: false, frozen: false },
  { field: "category",      header: "Category",     width: 100, align: "left",  mono: false, frozen: false },
  { field: "manufacturer",  header: "Manufacturer", width: 120, align: "left",  mono: false, frozen: false },
  // DISPLAY ONLY, AND IT STOPS HERE -- the distributor's own SKU, shown so a
  // buyer can see the number the supplier system expects on a PO. It is not a
  // Material field, it is not on any save wire, and nothing matches on it:
  // the same reference string can name two different parts at two sellers.
  // Same contract as the picker column -- see CatalogProduct.supplierRef.
  { field: "supplierRef",   header: "Supplier Ref", width: 120, align: "left",  mono: false, frozen: false },
  { field: "hsnCode",       header: "HSN Code",     width: 100, align: "left",  mono: true,  frozen: false },
  { field: "uom",           header: "UoM",          width:  64, align: "left",  mono: false, frozen: false },
  { field: "weight",        header: "Weight kg",    width: 100, align: "right", mono: true,  frozen: false },
  { field: "volume",        header: "Volume CBM",   width: 120, align: "right", mono: true,  frozen: false },
];

function frozenLefts(): number[] {
  const out: number[] = [];
  let acc = 0;
  for (const c of COLS) {
    if (c.frozen) { out.push(acc); acc += c.width; }
    else out.push(-1);
  }
  return out;
}

/** The one glyph this table uses for "the master does not carry this". */
function Dash() {
  return <span style={{ color: semanticColors.textPlaceholder }}>{UNSET_DASH}</span>;
}

/** A string field, or the dash. An empty string counts as absent:
 *  toCatalogProduct maps a null description to "", and a blank cell reads as
 *  a rendering glitch rather than as a fact about the record. */
function text(value: string | undefined) {
  return value && value.length > 0 ? <>{value}</> : <Dash />;
}

/** Weight and volume, or the dash.
 *
 *  ⚠️ ZERO IS DASHED TOO, AND THAT IS NOT THE "ABSENT IS NOT ZERO" RULE
 *  RUNNING BACKWARDS. anixify.product stores these as plain Floats with no
 *  null state, so an unmeasured row arrives as 0 -- the seeded product does
 *  exactly that, and the catalog XML says so in as many words: absent because
 *  NOT KNOWN, not because it weighs nothing. The backend cannot distinguish
 *  the two, so the dash is the honest rendering of the value it can send.
 *  Nothing computes from these here; this is a browse screen and both columns
 *  are display-only.
 *
 *  It also removes a real crash. The previous mock-typed version called
 *  `(value as number).toFixed(2)` unguarded, which throws a TypeError -- not
 *  a NaN -- the first time an optional number arrives undefined. */
function measurement(value: number | undefined, digits: number) {
  if (value === undefined || value === null || value === 0) return <Dash />;
  return <>{value.toFixed(digits)}</>;
}

// Exported so other tables (e.g. DutyRatesTable) can reuse it instead of
// rebuilding it. Widened to accept "All" since some callers' category field
// allows that value even though ProductCategory itself never does.
//
// ⚠️ IT HAS NO RENDERING FOR "NO CATEGORY". A CatalogProduct's category is
// nullable -- a product nobody has classified, or one carrying a name this
// build's closed union cannot resolve -- so every caller reading the real
// master must guard before reaching here, as this table and
// MaterialPickerTable both do.
export function CategoryPill({ cat }: { cat: ProductCategory | "All" }) {
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center",
        height: heights.pill, padding: `0 ${spacing[8]}px`, borderRadius: radius.base,
        fontSize: typography.fontSize.xs, color: semanticColors.textSecondary,
        backgroundColor: semanticColors.hoverBg, border: `1px solid ${semanticColors.border}`,
        fontWeight: typography.fontWeight.medium,
      }}
    >
      {cat}
    </span>
  );
}

export function CatalogTable({
  rows,
  height,
  onRowClick,
  selectedId,
  emptyMessage,
}: {
  rows: CatalogProduct[];
  height: number;
  onRowClick: (p: CatalogProduct) => void;
  // ⚠️ BY ID, NEVER BY PART NUMBER. Part numbers are not unique in this trade
  // and the backend deliberately has no constraint saying they are, so two
  // rows can legitimately share one -- selecting on it would highlight both
  // and open whichever the click happened to land on. Same reason the picker
  // keys its selection on id.
  selectedId?: number;
  // WHAT AN EMPTY TABLE SAYS. Passed in because only the page knows WHY it is
  // empty -- a search that matched nothing, or a filter hiding everything --
  // and one sentence cannot be right for both.
  emptyMessage: string;
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });

  const totalWidth = useMemo(() => COLS.reduce((s, c) => s + c.width, 0), []);
  const lefts = useMemo(() => frozenLefts(), []);

  return (
    <div
      data-testid="catalog-table-scroll"
      ref={scrollRef}
      style={{
        height, overflow: "auto",
        border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
        backgroundColor: semanticColors.surface, position: "relative", outline: "none",
      }}
    >
      <div style={{ width: totalWidth, position: "relative" }}>
        {/* HEADER */}
        <div
          data-testid="catalog-table-head"
          style={{
            position: "sticky", top: 0, zIndex: 3, display: "flex",
            height: HEADER_HEIGHT, backgroundColor: semanticColors.panelBg,
            borderBottom: `1px solid ${semanticColors.border}`,
          }}
        >
          {COLS.map((c, idx) => (
            <div
              key={c.field}
              style={{
                width: c.width, minWidth: c.width, padding: `0 ${spacing[12]}px`,
                display: "flex", alignItems: "center",
                justifyContent: c.align === "right" ? "flex-end" : "flex-start",
                fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: semanticColors.textSecondary,
                textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide,
                whiteSpace: "nowrap", backgroundColor: semanticColors.panelBg,
                position: c.frozen ? "sticky" : undefined,
                left: c.frozen ? lefts[idx] : undefined,
                zIndex: c.frozen ? 4 : 3,
                borderRight: c.frozen && idx === 1 ? `1px solid ${semanticColors.border}` : undefined,
              }}
            >
              {c.header}
            </div>
          ))}
        </div>

        {/* BODY */}
        <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
          {virtualizer.getVirtualItems().map((v) => {
            const row = rows[v.index];
            if (!row) return null;
            const isSelected = row.id === selectedId;
            return (
              <div
                // ⚠️ BY ID. Keying this on partNo handed React duplicate keys
                // the moment two real products shared a part number, which the
                // master permits.
                key={row.id}
                data-testid={`catalog-row-${row.id}`}
                data-part-no={row.partNo}
                className="material-row"
                onClick={() => onRowClick(row)}
                style={{
                  position: "absolute", top: 0, left: 0,
                  transform: `translateY(${v.start}px)`,
                  height: ROW_HEIGHT, width: totalWidth, display: "flex",
                  borderBottom: `1px solid ${semanticColors.border}`,
                  backgroundColor: isSelected ? semanticColors.hoverBg : semanticColors.surface,
                  cursor: "pointer",
                }}
              >
                {COLS.map((c, idx) => {
                  const isPartNo = idx === 0;
                  const cellBg = isSelected ? semanticColors.hoverBg : "inherit";

                  let content: React.ReactNode;
                  if (c.field === "category") {
                    // Guarded: CategoryPill has no rendering for "no
                    // category", and an unclassified product is a real state.
                    content = row.category ? <CategoryPill cat={row.category} /> : <Dash />;
                  } else if (c.field === "weight") {
                    content = measurement(row.weight, 2);
                  } else if (c.field === "volume") {
                    content = measurement(row.volume, 4);
                  } else if (c.field === "brand") {
                    // Null covers both "nobody classified it" and "a brand
                    // name this build's closed union cannot resolve". Neither
                    // is checked against a rule here -- /catalog evaluates no
                    // brand locks -- so it is stated, not warned about.
                    content = text(row.brand ?? undefined);
                  } else {
                    content = text(row[c.field] as string | undefined);
                  }

                  return (
                    <div
                      key={c.field}
                      style={{
                        width: c.width, minWidth: c.width, padding: `0 ${spacing[12]}px`,
                        display: "flex", alignItems: "center",
                        justifyContent: c.align === "right" ? "flex-end" : "flex-start",
                        fontSize: typography.fontSize.sm,
                        color: semanticColors.textPrimary,
                        fontWeight: isPartNo ? typography.fontWeight.medium : typography.fontWeight.regular,
                        fontVariantNumeric: c.mono ? "tabular-nums" : "normal",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                        position: c.frozen ? "sticky" : undefined,
                        left: c.frozen ? lefts[idx] : undefined,
                        zIndex: c.frozen ? 1 : 0,
                        backgroundColor: cellBg,
                        borderRight: c.frozen && idx === 1 ? `1px solid ${semanticColors.border}` : undefined,
                      }}
                    >
                      {content}
                    </div>
                  );
                })}
              </div>
            );
          })}
          {rows.length === 0 && (
            <div
              data-testid="catalog-empty"
              style={{
                position: "absolute", top: 0, left: 0, right: 0,
                padding: spacing[40], textAlign: "center",
                fontSize: typography.fontSize.base, color: semanticColors.textPlaceholder,
              }}
            >
              {emptyMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
